/**
 * Preserve a deep link's URL fragment across the SSO round-trip.
 *
 * The problem this solves is structural, not a bug in the redirect logic:
 * **browsers never transmit the fragment to the server.** RFC 7231 §5.5.2
 * requires a user agent to strip the fragment from `Referer`, and the server's
 * post-login redirect target (`originalURL`) is derived from exactly that
 * header. So for a link like
 *
 *     https://clear.comcast.net/project-fair/2026#west
 *
 * the server can only ever learn `/project-fair/2026`. The `#west` is held by
 * the browser alone and is lost the moment we navigate to the identity
 * provider. No amount of server-side work can recover it — the bytes never
 * arrive.
 *
 * The fix therefore has to be client-side: stash the fragment in
 * `sessionStorage` immediately before bouncing to SSO, then reapply it once we
 * land back on the same path.
 *
 * `sessionStorage` is the right store here: it is scoped per-origin *and*
 * per-tab, so it survives the off-origin trip to the identity provider and back
 * without leaking a half-finished navigation into the user's other tabs.
 */

const STORAGE_KEY = "clear.pendingReturnHash";

/**
 * Safe accessor — `sessionStorage` throws on access in some privacy modes and
 * sandboxed iframes. A lost fragment is a cosmetic downgrade; a thrown
 * exception here would break the login redirect itself, which is not.
 */
const getStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Record the fragment the user is currently on, just before leaving for SSO.
 *
 * Stores the path alongside it so the restore step can verify it is reapplying
 * the fragment to the page it was captured from. Without that check, a login
 * that falls back to `/` would have `#west` stapled onto it.
 *
 * No-ops when there is no fragment, so a normal login writes nothing.
 */
export const stashReturnHash = () => {
  const store = getStore();
  if (!store) return;

  const { pathname, search, hash } = window.location;
  if (!hash || hash === "#") return;

  try {
    store.setItem(STORAGE_KEY, JSON.stringify({ pathname, search, hash }));
  } catch {
    // Quota or serialization failure — fall back to today's behavior.
  }
};

/**
 * Reapply a stashed fragment after returning from SSO.
 *
 * Call this *before* React renders. Components read `window.location.hash` in
 * `useState` initializers (ProjectFair does exactly this to pick its chapter),
 * so restoring the fragment first means the first paint is already correct —
 * no flash of the "choose an event" prompt, and no second render.
 *
 * This is deliberately one-shot: the entry is cleared on read, whether or not
 * it is used. Otherwise a stale fragment would reattach itself to a later
 * manual visit to the same path, which looks like the page ignoring the user.
 *
 * @returns {boolean} true when a fragment was reapplied.
 */
export const restoreReturnHash = () => {
  const store = getStore();
  if (!store) return false;

  let raw = null;
  try {
    raw = store.getItem(STORAGE_KEY);
    // Read-and-clear: consumed exactly once regardless of the outcome below.
    if (raw !== null) store.removeItem(STORAGE_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;

  let stashed;
  try {
    stashed = JSON.parse(raw);
  } catch {
    return false;
  }
  if (!stashed || typeof stashed.hash !== "string" || !stashed.hash) return false;

  // Only reapply to the page the fragment came from. A login that fell back to
  // "/" — or a session that expired on a different page — must not inherit it.
  if (stashed.pathname !== window.location.pathname) return false;

  // Already present (e.g. the browser preserved it across a same-origin
  // bounce): nothing to do, and rewriting would be a redundant history touch.
  if (window.location.hash === stashed.hash) return false;

  // `replaceState` rather than assigning `location.hash`: this must not add a
  // history entry (Back would otherwise land on the fragment-less URL the user
  // never chose) and must not trigger navigation.
  const { pathname, search } = window.location;
  window.history.replaceState(null, "", `${pathname}${search}${stashed.hash}`);
  return true;
};

/**
 * Send the user to SSO, preserving their current fragment.
 *
 * Every path that redirects to `/auth/login` should go through this so the
 * capture can never be forgotten at one of the call sites.
 *
 * Assigns `location.href` rather than calling `location.assign()` purely to
 * match the existing call sites this replaces — the redirect behavior is
 * identical, and keeping it means the established interceptor tests continue to
 * exercise this path unchanged.
 */
export const redirectToLogin = () => {
  stashReturnHash();
  window.location.href = "/auth/login";
};
